(() => {
  const controllerKey = Symbol.for('vorug.sizeGuide');
  if (window[controllerKey]) return;
  window[controllerKey] = true;
  const instances = new WeakMap();
  const initialize = (guide) => {
    if (instances.has(guide)) return;
    const lifecycle = new AbortController();
    const { signal } = lifecycle;
    const delayed = new Set();
    const later = (callback, delay) => {
      const id = window.setTimeout(() => { delayed.delete(id); callback(); }, delay);
      delayed.add(id);
    };
    let headerObserver;
    let navObserver;
    instances.set(guide, () => {
      lifecycle.abort();
      headerObserver?.disconnect();
      navObserver?.disconnect();
      delayed.forEach((id) => window.clearTimeout(id));
      window.cancelAnimationFrame(activeFrame);
      window.cancelAnimationFrame(headerSyncFrame);
      instances.delete(guide);
    });
    const storageKey = 'vorug-size-unit';
    const buttons = Array.from(guide.querySelectorAll('[data-vsg-unit]'));
    const labels = Array.from(guide.querySelectorAll('[data-vsg-size]'));
    const roomNav = guide.querySelector('.vsg-anchor-nav');
    const roomNavTrack = roomNav?.querySelector('.vsg-anchor-nav__links');
    const roomNavSentinel = guide.querySelector('.vsg-anchor-sentinel');
    const roomLinks = Array.from(guide.querySelectorAll('.vsg-anchor-nav a[href^="#"]'));
    const jumpLinks = [...roomLinks, ...guide.querySelectorAll('.vsg-converter__sizes a[href^="#"]')];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const rooms = roomLinks
      .map((link) => guide.querySelector(link.getAttribute('href')))
      .filter(Boolean);
    const mobileMedia = window.matchMedia('(max-width: 719px)');

    const trimDecimal = (value) => String(value).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');

    const metricLabel = (label) => {
      const parts = String(label).split(/\s*[x×]\s*/i);
      if (parts.length !== 2) return label;
      const values = parts.map((part) => {
        const match = part.trim().match(/^(\d+(?:\.\d+)?)\s*(?:['’′])?\s*(?:(\d+)\s*(?:["”″]))?$/);
        if (!match) return null;
        return (Number(match[1]) * 12 + Number(match[2] || 0)) * 0.0254;
      });
      if (values.some((value) => value === null || !Number.isFinite(value))) return label;
      return `${trimDecimal(values[0].toFixed(2))} × ${trimDecimal(values[1].toFixed(2))}m`;
    };

    const setUnit = (unit) => {
      const nextUnit = unit === 'm' ? 'm' : 'ft';
      try {
        localStorage.setItem(storageKey, nextUnit);
      } catch (error) {
        // Conversion remains available when storage is blocked.
      }
      buttons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.vsgUnit === nextUnit));
      });
      labels.forEach((label) => {
        label.textContent = nextUnit === 'm' ? metricLabel(label.dataset.sizeFt) : label.dataset.sizeFt;
      });
    };

    let initialUnit = 'ft';
    try {
      if (localStorage.getItem(storageKey) === 'm') initialUnit = 'm';
    } catch (error) {
      initialUnit = 'ft';
    }

    buttons.forEach((button) => button.addEventListener('click', () => setUnit(button.dataset.vsgUnit), { signal }));
    setUnit(initialUnit);

    let activeFrame = null;
    const themeHeaderSelector =
      '#shopify-section-mobile-header, .shopify-section-mobile-header, #shopify-section-header, .shopify-section-header, .shopify-section-group-header-group, [data-sticky-header], sticky-header, header.header, header.mobile-header, .announcement-bar';
    const getThemeHeaderCandidates = () => Array.from(document.querySelectorAll(themeHeaderSelector));

    const getThemeHeaderOffset = () => {
      const candidates = getThemeHeaderCandidates();
      let bottom = 0;

      candidates.forEach((element) => {
        if (guide.contains(element)) return;
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') return;
        if (style.position !== 'fixed' && style.position !== 'sticky') return;
        const bounds = element.getBoundingClientRect();
        if (bounds.height <= 0 || bounds.top > 2 || bounds.bottom <= 0) return;
        if (bounds.bottom > window.innerHeight * 0.35) return;
        bottom = Math.max(bottom, bounds.bottom);
      });

      return Math.max(0, Math.round(bottom));
    };

    const updateStickyRoomNav = () => {
      if (!roomNav || !roomNavSentinel) return 0;
      const headerOffset = getThemeHeaderOffset();
      guide.style.setProperty('--vsg-sticky-offset', `${headerOffset}px`);
      const lastRoom = rooms[rooms.length - 1];
      const shouldStick = roomNavSentinel.getBoundingClientRect().top < window.innerHeight * 0.7 &&
        lastRoom?.getBoundingClientRect().bottom > headerOffset + 100;
      const previewBar = document.querySelector('#PBarNextFrameWrapper, #preview-bar-iframe');
      const previewBounds = previewBar?.getBoundingClientRect();
      const previewInset = previewBounds && previewBounds.height > 0 && previewBounds.height < window.innerHeight / 2 && previewBounds.top < window.innerHeight
        ? Math.max(0, window.innerHeight - previewBounds.top) : 0;
      guide.style.setProperty('--vsg-preview-inset', `${previewInset}px`);
      roomNav.classList.toggle('is-stuck', shouldStick);
      roomNav.inert = !shouldStick;
      return headerOffset;
    };

    roomNav?.classList.add('is-enhanced');
    let previousActiveLink = null;
    const updateActiveRoom = () => {
      activeFrame = null;
      const headerOffset = updateStickyRoomNav();
      const guideLine = headerOffset + Math.min(160, window.innerHeight * 0.2);
      const activeRoom = rooms.find((room) => {
        const bounds = room.getBoundingClientRect();
        return bounds.top <= guideLine && bounds.bottom > guideLine;
      });

      let activeLink = null;
      roomLinks.forEach((link) => {
        if (activeRoom && link.getAttribute('href') === `#${activeRoom.id}`) {
          link.setAttribute('aria-current', 'location');
          activeLink = link;
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (activeLink && activeLink !== previousActiveLink && roomNavTrack && roomNavTrack.scrollWidth > roomNavTrack.clientWidth) {
        const trackBounds = roomNavTrack.getBoundingClientRect();
        const linkBounds = activeLink.getBoundingClientRect();
        const left = Math.max(0, roomNavTrack.scrollLeft + linkBounds.left - trackBounds.left - (trackBounds.width - linkBounds.width) / 2);
        roomNavTrack.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      previousActiveLink = activeLink;
    };

    const requestActiveRoomUpdate = () => {
      if (activeFrame !== null) return;
      activeFrame = window.requestAnimationFrame(updateActiveRoom);
    };

    let headerSyncFrame = null;
    let headerSyncUntil = 0;

    const syncRoomNavWithHeaderMotion = () => {
      headerSyncUntil = window.performance.now() + 350;
      if (headerSyncFrame !== null) return;

      const syncFrame = () => {
        headerSyncFrame = null;
        updateStickyRoomNav();
        if (window.performance.now() < headerSyncUntil) {
          headerSyncFrame = window.requestAnimationFrame(syncFrame);
        } else {
          requestActiveRoomUpdate();
        }
      };

      headerSyncFrame = window.requestAnimationFrame(syncFrame);
    };

    const themeHeaders = getThemeHeaderCandidates();
    if ('MutationObserver' in window) {
      headerObserver = new MutationObserver(syncRoomNavWithHeaderMotion);
      themeHeaders.forEach((header) => {
        headerObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
      });
    }
    themeHeaders.forEach((header) => {
      header.addEventListener('transitionrun', syncRoomNavWithHeaderMotion, { signal });
      header.addEventListener('transitionend', syncRoomNavWithHeaderMotion, { signal });
      header.addEventListener('animationstart', syncRoomNavWithHeaderMotion, { signal });
      header.addEventListener('animationend', syncRoomNavWithHeaderMotion, { signal });
    });

    const scrollToRoom = (room, behavior = 'smooth') => {
      if (!room || !roomNav) return;
      const headerOffset = updateStickyRoomNav();
      const gap = room.classList.contains('vsg-card') ? 12 : 0;
      const anchor = room.querySelector('.vsg-photo-banner') || room;
      const top = window.scrollY + anchor.getBoundingClientRect().top - headerOffset - gap;
      window.scrollTo({ top: Math.max(0, top), behavior });
    };

    if (roomNav && 'ResizeObserver' in window) {
      navObserver = new ResizeObserver(requestActiveRoomUpdate);
      navObserver.observe(roomNav);
    }

    window.addEventListener('scroll', requestActiveRoomUpdate, { passive: true, signal });
    window.addEventListener('resize', requestActiveRoomUpdate, { signal });
    if (typeof mobileMedia.addEventListener === 'function') {
      mobileMedia.addEventListener('change', requestActiveRoomUpdate, { signal });
    }
    jumpLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const room = guide.querySelector(link.getAttribute('href'));
        if (!room) return;
        room.focus({ preventScroll: true });
        const behavior = reducedMotion.matches ? 'auto' : 'smooth';
        later(() => scrollToRoom(room, behavior), 0);
        try {
          window.history.replaceState(null, '', link.getAttribute('href'));
        } catch (error) {
          // The navigation still scrolls correctly when history updates are blocked.
        }
        const settleRoom = () => {
          scrollToRoom(room, 'auto');
          requestActiveRoomUpdate();
        };
        later(settleRoom, behavior === 'smooth' ? 500 : 0);
      }, { signal });
    });

    const hashTargets = [...guide.querySelectorAll('.vsg-photo-section[id], .vsg-card[id]')];
    const targetFromHash = () => hashTargets.find((room) => `#${room.id}` === window.location.hash);
    window.addEventListener('hashchange', () => {
      const target = targetFromHash();
      if (!target) return;
      later(() => {
        scrollToRoom(target, 'auto');
        requestActiveRoomUpdate();
      }, 0);
    }, { signal });

    const initialRoom = targetFromHash();
    if (initialRoom) {
      const alignInitialRoom = () => later(() => scrollToRoom(initialRoom, 'auto'), 0);
      if (document.readyState === 'complete') {
        alignInitialRoom();
      } else {
        window.addEventListener('load', alignInitialRoom, { once: true, signal });
      }
    }
    updateActiveRoom();
  };
  const initializeWithin = (root) => {
    root.querySelectorAll('[data-vorug-size-guide]').forEach(initialize);
  };
  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-vorug-size-guide]').forEach((guide) => instances.get(guide)?.());
  });
})();
